import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaAws, 
  FaGoogle, 
  FaMicrosoft, 
  FaDatabase, 
  FaChartLine,
  FaProjectDiagram,
  FaBusinessTime,
  FaGraduationCap,
  FaLaptopCode,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaArrowLeft
} from 'react-icons/fa';
import Footer from './Footer';
import '../Styling/Services.css';

const ITTraining = () => {
  useEffect(() => {
    document.title = 'IT Training - QuantifyLogic';
    window.scrollTo(0, 0);
  }, []);

  const stats = [
    { icon: <FaGraduationCap />, number: "10,000+", text: "Certified Professionals" },
    { icon: <FaLaptopCode />, number: "50+", text: "Expert Trainers" }
  ];

  const certifications = [
    {
      id: 1,
      title: "Microsoft Azure Training",
      icon: <FaMicrosoft className="cert-icon" />,
      description: "Comprehensive Azure certification training covering Azure Administrator, Developer, Solutions Architect, and DevOps Engineer paths.",
      courses: [
        "AZ-900: Microsoft Azure Fundamentals",
        "AZ-104: Microsoft Azure Administrator",
        "AZ-204: Developing Solutions for Microsoft Azure",
        "AZ-305: Designing Microsoft Azure Infrastructure Solutions"
      ],
      level: "Beginner to Advanced"
    },
    {
      id: 2,
      title: "AWS Certification",
      icon: <FaAws className="cert-icon" />,
      description: "Industry-leading AWS certification training for cloud computing roles, from foundational to professional levels.",
      courses: [
        "AWS Cloud Practitioner",
        "AWS Solutions Architect Associate",
        "AWS Developer Associate",
        "AWS SysOps Administrator"
      ],
      level: "Beginner to Professional"
    },
    {
      id: 3,
      title: "Google Cloud Platform (GCP)",
      icon: <FaGoogle className="cert-icon" />,
      description: "Expert-led GCP certification training to master Google's cloud infrastructure and services.",
      courses: [
        "Google Cloud Digital Leader",
        "Google Cloud Engineer",
        "Google Cloud Architect",
        "Google Cloud Developer"
      ],
      level: "Intermediate to Advanced"
    },
    {
      id: 4,
      title: "Oracle Certification",
      icon: <FaDatabase className="cert-icon" />,
      description: "Comprehensive Oracle certification programs covering database administration, development, and cloud services.",
      courses: [
        "Oracle Database SQL",
        "Oracle Database Administration",
        "Oracle Cloud Infrastructure",
        "Oracle Java Certification"
      ],
      level: "Intermediate to Expert"
    },
    {
      id: 5,
      title: "PMI Certification",
      icon: <FaProjectDiagram className="cert-icon" />,
      description: "Professional project management certification training aligned with PMI standards and best practices.",
      courses: [
        "Project Management Professional (PMP)",
        "PMI Agile Certified Practitioner (PMI-ACP)",
        "Program Management Professional (PgMP)",
        "Portfolio Management Professional (PfMP)"
      ],
      level: "Professional"
    },
    {
      id: 6,
      title: "IIBA Certification",
      icon: <FaBusinessTime className="cert-icon" />,
      description: "Business analysis certification training following IIBA's globally recognized standards.",
      courses: [
        "Entry Certificate in Business Analysis (ECBA)",
        "Certification of Capability in Business Analysis (CCBA)",
        "Certified Business Analysis Professional (CBAP)",
        "Agile Analysis Certification (AAC)"
      ],
      level: "Beginner to Professional"
    }
  ];

  return (
    <motion.div
      className="it-training-wrapper"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="service-navigation-wrapper">
        <button 
          className="nav-button back-button"
          onClick={() => window.history.back()}
          aria-label="Go back"
        >
          <FaArrowLeft />
          <span>Back</span>
        </button>
      </div>
      <div className="service-component-wrapper">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="service-component-hero"
          style={{
            background: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7))`,
            
          }}
        >
          <div className="service-hero-content">
            <h1 className="service-hero-title">IT Training & Certification</h1>
            <p className="service-hero-subtitle">Empower your career with industry-recognized certifications. Our comprehensive training programs are designed to help you master the latest technologies and best practices.</p>
          </div>
        </motion.div>

        <div className="stats-section">
          <motion.div 
            className="stats-grid"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="stat-card"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <div className="stat-icon">{stat.icon}</div>
                <h3>{stat.number}</h3>
                <p>{stat.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        <div className="training-container">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="training-grid"
          >
            {certifications.map((cert, index) => (
              <motion.div
                key={cert.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="certification-card"
                whileHover={{ y: -10 }}
              >
                <div className="cert-header">
                  {cert.icon}
                  <h2>{cert.title}</h2>
                </div>
                <p className="cert-description">{cert.description}</p>
                <div className="cert-meta">
                  <span className="level">Level: {cert.level}</span>
                </div>
                <div className="course-list">
                  <h3>Available Courses:</h3>
                  <ul>
                    {cert.courses.map((course, idx) => (
                      <motion.li
                        key={idx}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 * idx }}
                      >
                        {course}
                      </motion.li>
                    ))}
                  </ul>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="enroll-button"
                >
                  Enroll Now
                </motion.button>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="training-cta"
          >
            <h2>Ready to Get Certified?</h2>
            <p>Join our expert-led training programs and take the first step towards advancing your career.</p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="cta-button"
            >
              Schedule a Consultation
            </motion.button>
          </motion.div>
        </div>
      </div>
      <Footer />
    </motion.div>
  );
};

export default ITTraining; 